/**
 * Canonical filter vocabulary shared across the product.
 *
 * One operator set + one filter shape powers the reporting builder today, and is designed so the data-table
 * filter system (`TableFilterFieldConfig` / `table-query.ts`) can adopt it later without diverging. Defining it
 * once here is what lets ad-hoc table filtering and full reports speak the same language instead of duplicating
 * operator/value logic.
 *
 * NOTE: the string values of the shared operators that already existed in reporting (`equals`, `notEquals`,
 * `contains`, `in`, `notIn`, `between`, `gt`, `gte`, `lt`, `lte`) are preserved verbatim so existing saved-report
 * definitions remain valid — reporting re-exports `FilterOperator` as `ReportFilterOperator`.
 */
export enum FilterOperator {
  EQUALS = "equals",
  NOT_EQUALS = "notEquals",
  CONTAINS = "contains",
  NOT_CONTAINS = "notContains",
  IN = "in",
  NOT_IN = "notIn",
  BETWEEN = "between",
  GT = "gt",
  GTE = "gte",
  LT = "lt",
  LTE = "lte",
  IS_NULL = "isNull",
  IS_NOT_NULL = "isNotNull",
  /** value is a {@link RelativeDatePreset} resolved to a concrete range at execution time. */
  RELATIVE_DATE = "relativeDate",
}

/** The kind of value a filterable field holds — drives which operators + input control the UI offers. */
export enum FilterFieldType {
  TEXT = "text",
  NUMBER = "number",
  CURRENCY = "currency",
  DATE = "date",
  DATETIME = "datetime",
  BOOLEAN = "boolean",
  SELECT = "select",
  MULTI_SELECT = "multiSelect",
}

/** Named, server-resolved date windows so a non-technical user picks "Last 90 days" instead of two dates. */
export enum RelativeDatePreset {
  TODAY = "today",
  YESTERDAY = "yesterday",
  THIS_WEEK = "thisWeek",
  LAST_WEEK = "lastWeek",
  THIS_MONTH = "thisMonth",
  LAST_MONTH = "lastMonth",
  THIS_QUARTER = "thisQuarter",
  LAST_QUARTER = "lastQuarter",
  THIS_YEAR = "thisYear",
  LAST_YEAR = "lastYear",
  LAST_7_DAYS = "last7Days",
  LAST_30_DAYS = "last30Days",
  LAST_90_DAYS = "last90Days",
  LAST_365_DAYS = "last365Days",
}

export type FilterScalar = string | number | boolean;

/**
 * The value carried by a {@link FilterSpec}, shaped by the operator:
 * - scalar for EQUALS/GT/…; array for IN/NOT_IN; 2-tuple for BETWEEN; {@link RelativeDatePreset} for RELATIVE_DATE;
 * - omitted/null for IS_NULL/IS_NOT_NULL.
 */
export type FilterValue =
  | FilterScalar
  | null
  | FilterScalar[]
  | [FilterScalar | null, FilterScalar | null]
  | RelativeDatePreset;

/** A single applied filter — the atom both reports and (later) table filters serialize. */
export interface FilterSpec {
  field: string;
  operator: FilterOperator;
  value?: FilterValue;
}

export interface FilterFieldOption {
  value: string;
  labelKey: string;
}

/** UI descriptor for a filterable field: enough for the builder (and later tables) to render a filter row. */
export interface FilterFieldSpec {
  field: string;
  labelKey: string;
  type: FilterFieldType;
  /** Allowed operators; defaults to {@link DEFAULT_FILTER_OPERATORS} for the type when omitted. */
  operators?: FilterOperator[];
  defaultOperator?: FilterOperator;
  /** For SELECT / MULTI_SELECT. */
  options?: FilterFieldOption[];
  required?: boolean;
}

/** Single source of truth for which operators a field type offers, so the builder and tables stay consistent. */
export const DEFAULT_FILTER_OPERATORS: Record<FilterFieldType, FilterOperator[]> = {
  [FilterFieldType.TEXT]: [
    FilterOperator.CONTAINS,
    FilterOperator.EQUALS,
    FilterOperator.NOT_EQUALS,
    FilterOperator.IS_NULL,
    FilterOperator.IS_NOT_NULL,
  ],
  [FilterFieldType.NUMBER]: [
    FilterOperator.EQUALS,
    FilterOperator.GT,
    FilterOperator.GTE,
    FilterOperator.LT,
    FilterOperator.LTE,
    FilterOperator.BETWEEN,
  ],
  [FilterFieldType.CURRENCY]: [
    FilterOperator.EQUALS,
    FilterOperator.GT,
    FilterOperator.GTE,
    FilterOperator.LT,
    FilterOperator.LTE,
    FilterOperator.BETWEEN,
  ],
  [FilterFieldType.DATE]: [
    FilterOperator.RELATIVE_DATE,
    FilterOperator.BETWEEN,
    FilterOperator.GTE,
    FilterOperator.LTE,
    FilterOperator.EQUALS,
  ],
  [FilterFieldType.DATETIME]: [
    FilterOperator.RELATIVE_DATE,
    FilterOperator.BETWEEN,
    FilterOperator.GTE,
    FilterOperator.LTE,
  ],
  [FilterFieldType.BOOLEAN]: [FilterOperator.EQUALS],
  [FilterFieldType.SELECT]: [
    FilterOperator.EQUALS,
    FilterOperator.NOT_EQUALS,
    FilterOperator.IN,
    FilterOperator.NOT_IN,
  ],
  [FilterFieldType.MULTI_SELECT]: [FilterOperator.IN, FilterOperator.NOT_IN],
};

/** Resolve the operators a field offers (explicit list or the type default). */
export function getFilterOperators(field: FilterFieldSpec): FilterOperator[] {
  return field.operators ?? DEFAULT_FILTER_OPERATORS[field.type];
}
